/**
 * 센터소개 페이지 컴포넌트
 * 4가지 서브메뉴(인사말, 연혁, 조직안내, 오시는길)를 제공하는 페이지입니다.
 * URL 파라미터에 따라 다른 콘텐츠를 동적으로 표시합니다.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import SubPage from '../template/SubPage';
import Map from '../components/Map';
import History from '../components/History';
import { getDirections } from '../Api';
import { SeemPowerIntro, seemPowerHistory } from '../constant/introduceData';
import { MENU_DATA, getSubMenuLabel } from '../constant/menuData';
import seemLogo from '../assets/seem_logo_w.png';
import listIcon from '../assets/list.png';
import MyHelmet from '../hooks/Helmet';
import orgaImage from '../assets/newImage/organization@3x.png';

function IntroducePage() {
  const no = useParams().no;
  const [submenu, setSubMenu] = useState('법인 인사말');
  const [address, setAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [subway, setSubway] = useState([]);
  const [bus, setBus] = useState([]);

  // 메뉴 데이터 가져오기
  const menuData = MENU_DATA.introduce;

  useEffect(() => {
    setSubMenu(getSubMenuLabel('introduce', no) || '법인 인사말');

    if (no === '4') {
      // 오시는길 - API에서 위치 정보 가져오기
      getDirections()
        .then((response) => {
          setAddress(response.address);
          setPhoneNumber(response.phoneNumber);
          setSubway(response.subway || []);
          setBus(response.bus || []);
        })
        .catch((error) => {
          console.log('API 호출 실패:', error);
          // API 실패 시 기본값 사용 (로컬 개발 환경 대응)
          setAddress('(우)04574 서울시 중구 난계로185, 8층');
          setPhoneNumber('02-2233-3789');
          setSubway(['2호선 신당역 2번출구 500m']);
          setBus(['241번, 421번', '황학동주민센터']);
        });
    }
  }, [no]);

  // 콘텐츠 렌더링
  const renderContent = () => {
    if (no === '1') {
      // 법인 인사말
      const authText = SeemPowerIntro.auth;
      const authPrefix = authText.slice(0, -3); // 마지막 3글자 제외
      const authName = authText.slice(-3); // 마지막 3글자 (이름)

      return (
        <GreetingContainer>
          <LogoImage src={seemLogo} alt="에스이임파워 로고" />
          <ContentWrapper>
            <GreetingTitle>{SeemPowerIntro.title}</GreetingTitle>
            <GreetingText>{SeemPowerIntro.text}</GreetingText>
            <HighlightText>{SeemPowerIntro.highlight}</HighlightText>
            <AuthText>
              {authPrefix}
              <AuthName>{authName}</AuthName>
            </AuthText>
          </ContentWrapper>
        </GreetingContainer>
      );
    } else if (no === '2') {
      // 법인 연혁
      return <History historyData={seemPowerHistory} />;
    }
    //  else if (no === '3') {
    //   // 중구 인사말
    //   return (
    //     <GreetingContainer>
    //       <LogoImage src={mainLogo} alt="중구행복돌봄센터 로고" />
    //       <ContentWrapper>
    //         <GreetingTitle>{JoongguIntro.title}</GreetingTitle>
    //         <GreetingText>{JoongguIntro.text}</GreetingText>
    //         <HighlightText>{JoongguIntro.highlight}</HighlightText>
    //       </ContentWrapper>
    //     </GreetingContainer>
    //   );
    // } else if (no === '4') {
    //   // 중구 연혁
    //   return <History historyData={joongguHistory} />;
    // }
    else if (no === '3') {
      // 조직안내
      return <OrgImage src={orgaImage} alt="조직안내" />;
    } else if (no === '4') {
      // 오시는길
      return (
        <DirectionsContainer>
          <Map address={address} />
          <InfoList>
            <InfoSection>
              <InfoTitle>주소</InfoTitle>
              <InfoSubList>
                <AddressItem>{address}</AddressItem>
                <AddressItem>TEL : {phoneNumber}</AddressItem>
              </InfoSubList>
            </InfoSection>
            <InfoSection>
              <InfoTitle>대중교통</InfoTitle>
              <InfoSubList>
                <TransportSection>
                  <TransportLabel>지하철</TransportLabel>
                  <TransportList>
                    {subway.map((item, index) => (
                      <TransportItem key={index}>
                        - &nbsp; &nbsp;{item}
                      </TransportItem>
                    ))}
                  </TransportList>
                </TransportSection>
                <TransportSection>
                  <TransportLabel>버스</TransportLabel>
                  <TransportList>
                    {bus.map((item, index) => (
                      <TransportItem key={index}>
                        - &nbsp; &nbsp;{item}
                      </TransportItem>
                    ))}
                  </TransportList>
                </TransportSection>
              </InfoSubList>
            </InfoSection>
          </InfoList>
        </DirectionsContainer>
      );
    }
  };

  return (
    <>
      <MyHelmet />
      <SubPage
        title={menuData.label}
        description={menuData.description}
        menuItems={menuData.subMenus}
        currentNo={no}
        submenuTitle={submenu}
        breadcrumbMenu={menuData.label}
        breadcrumbLink={menuData.path}
      >
        {renderContent()}
      </SubPage>
    </>
  );
}

export default IntroducePage;

/* Styled Components */

// 인사말 스타일
const GreetingContainer = styled.div`
  /* 데스크톱 기본 스타일 */
  display: flow-root;
  margin-top: 50px;
  width: 100%;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    margin-top: 30px;
    padding: 0 5%;
  }
`;

const LogoImage = styled.img`
  /* 데스크톱 기본 스타일 */
  float: right;
  width: 260px;
  height: 260px;
  margin: 0 15px 20px 30px;
  opacity: 0.5;
  object-fit: contain;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    float: none;
    width: 150px;
    height: 150px;
    margin: 0 auto 20px;
    display: block;
    opacity: 0.4;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    width: 250px;
    height: 250px;
  }
`;

const ContentWrapper = styled.div``;

const GreetingTitle = styled.h1`
  /* 데스크톱 기본 스타일 */
  font-size: 30px;
  font-family: 'koPubWorld DotumB';
  color: #333333;
  margin-bottom: 20px;
  white-space: pre-line;
  word-break: keep-all;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 24px;
    text-align: center;
  }

  @media (max-width: 375px) {
    font-size: 16px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 32px;
  }
`;

const GreetingText = styled.p`
  /* 데스크톱 기본 스타일 */
  font-size: 17px;
  font-family: 'koPubWorld DotumM';
  color: #333333;
  margin: 0 0 20px 0;
  white-space: pre-line;
  line-height: 1.8;
  word-break: keep-all;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 13px;
    text-align: left;
  }

  @media (min-width: 375px) and (max-width: 1023px) {
    font-size: 15px;
  }

  @media (min-width: 425px) and (max-width: 1023px) {
    font-size: 17px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 25px;
  }
`;

const HighlightText = styled.p`
  /* 데스크톱 기본 스타일 */
  font-family: 'koPubWorld DotumB';
  color: #2679b2;
  font-size: 19px;
  margin: 30px 0 0 0;
  white-space: pre-line;
  line-height: 1.7;
  clear: both;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 15px;
    margin: 20px 0 0 0;
  }

  @media (min-width: 375px) and (max-width: 1023px) {
    font-size: 17px;
  }

  @media (min-width: 425px) and (max-width: 1023px) {
    font-size: 19px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 28px;
  }
`;

const AuthText = styled.p`
  /* 데스크톱 기본 스타일 */
  font-family: 'koPubWorld DotumM';
  color: #333333;
  font-size: 17px;
  margin: 70px 0 0 0;
  text-align: right;
  clear: both;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 13px;
    margin: 40px 0 0 0;
  }

  @media (min-width: 375px) and (max-width: 1023px) {
    font-size: 15px;
  }

  @media (min-width: 425px) and (max-width: 1023px) {
    font-size: 17px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 25px;
  }
`;

const AuthName = styled.span`
  font-family: 'koPubWorld DotumB';
  font-weight: bold;
`;

// 조직안내 스타일
const OrgImage = styled.img`
  /* 데스크톱 기본 스타일 */
  width: 100%;
  height: auto;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    max-width: 90%;
    margin: 50px auto 0;
    display: block;
  }
`;

// 오시는길 스타일
const DirectionsContainer = styled.div`
  /* 데스크톱 기본 스타일 */
  width: 100%;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    padding: 0 5%;
  }
`;

const InfoList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoSection = styled.li`
  /* 데스크톱 기본 스타일 */
  margin-top: 20px;
`;

const InfoTitle = styled.h1`
  /* 데스크톱 기본 스타일 */
  background: url(${listIcon}) left no-repeat;
  background-size: 20px 20px;
  color: #e5857c;
  font-size: 20px;
  padding-left: 30px;
  margin-bottom: 18px;
  font-family: 'Nanum Gothic Bold';

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 17px;
    background-size: 17px 17px;
    padding-left: 25px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 24px;
    background-size: 24px 24px;
    padding-left: 35px;
  }
`;

const InfoSubList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AddressItem = styled.li`
  /* 데스크톱 기본 스타일 */
  margin-top: 10px;
  color: #333333;
  font-family: 'koPubWorld DotumB';
  font-size: 16px;
  font-weight: bold;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 14px;
    margin-left: 5%;
  }

  @media (min-width: 375px) and (max-width: 1023px) {
    font-size: 16px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 18px;
    margin-left: 7%;
  }
`;

const TransportSection = styled.li`
  /* 데스크톱 기본 스타일 */
  list-style: disc;
  margin-left: 30px;
  font-family: 'koPubWorld DotumB';
  margin-top: 10px;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    margin-left: 7%;
  }
`;

const TransportLabel = styled.p`
  /* 데스크톱 기본 스타일 */
  font-size: 16px;
  padding-left: 10px;
  color: #333333;
  margin: 0;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 15px;
    padding-left: 5px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 20px;
  }
`;

const TransportList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TransportItem = styled.li`
  /* 데스크톱 기본 스타일 */
  padding-left: 10px;
  color: #333333;
  font-size: 16px;
  margin-top: 5px;

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    font-size: 14px;
    padding-left: 5px;
  }

  @media (min-width: 375px) and (max-width: 1023px) {
    font-size: 16px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 18px;
  }
`;
